import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./contentTracking.css";
import CircularProgress from "@material-ui/core/CircularProgress";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import DashboardIcon from "@material-ui/icons/Dashboard";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";

import SideBar from "../../components/SideBar";
import BackButton from "../../components/Base/BackButton";
import { Components } from "../../assets/Dimensions";
import { FB, fmtNum } from "../../assets/feedbackTheme";
import ResponseStatus from "../../enums/ResponseStatus";
import { logoutUser } from "../../redux/actions/userAction.js";
import { getQuestionTracking, getVideoTracking } from "../../api/ContentTracking";
import { GetAllSegments } from "../../api/CMS";

import TrackingTable from "./TrackingTable";
import DetailDrawer from "./DetailDrawer";
import {
  buildClassOptions,
  buildCourseLabels,
  mergeTrackingRows,
  TABS,
  IDENTITY_KEYS,
  deriveRow,
  applyColumnFilters,
  columnOptionLabel,
  columnSortValue,
  matchesSearch,
} from "./tracking";

// İçerik Takip — PM's customer demo (pakodemy-content-tracking) ported into the
// admin panel in the Feedbacks visual language. Kazanım-level tracking of the
// lesson-video and question pipelines for one class at a time.

const TAB_ICONS = { general: DashboardIcon, questions: HelpOutlineIcon, videos: VideoLibraryIcon };

const styles = {
  container: {
    marginLeft: Components.SideBarWidth + 15,
    paddingTop: 20,
    marginRight: 30,
    paddingBottom: 40,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  title: { fontSize: 22, fontWeight: 600, color: FB.text, margin: 0 },
  subtitle: { fontSize: 13, color: FB.textMuted, marginTop: 2 },
  headerActions: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  selectWrap: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: FB.textSecondary },
  select: {
    height: 32,
    padding: "0 8px",
    fontSize: 13,
    color: FB.text,
    border: `1px solid ${FB.borderStrong}`,
    borderRadius: 8,
    backgroundColor: FB.surface,
    cursor: "pointer",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
    padding: "6px 12px",
    borderRadius: 8,
    border: `1px solid ${FB.borderStrong}`,
    backgroundColor: FB.surface,
    color: FB.text,
    cursor: "pointer",
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
    gap: 12,
    margin: "16px 0",
  },
  kpiCard: {
    backgroundColor: FB.surfaceAlt,
    borderRadius: 10,
    padding: "14px 16px",
    border: `1px solid ${FB.border}`,
  },
  kpiLabel: { fontSize: 13, color: FB.textSecondary },
  kpiValue: { fontSize: 24, fontWeight: 600, color: FB.text, marginTop: 4, minHeight: 30 },
  kpiHint: { fontSize: 12, color: FB.textMuted, marginTop: 2 },
  kpiCaption: { fontSize: 11, fontStyle: "italic", marginTop: 4 },
  tabs: {
    display: "flex",
    gap: 4,
    borderBottom: `1px solid ${FB.border}`,
    marginBottom: 12,
  },
  tab: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 14px",
    marginBottom: -1,
    border: "none",
    borderBottom: `2px solid ${active ? FB.accent : "transparent"}`,
    background: "none",
    fontSize: 14,
    fontWeight: active ? 600 : 500,
    color: active ? FB.accent : FB.textSecondary,
    cursor: "pointer",
  }),
  searchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    margin: "4px 0 12px",
  },
  searchLeft: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", flex: 1 },
  searchWrap: { position: "relative", flex: 1, minWidth: 220, maxWidth: 360 },
  searchInput: {
    width: "100%",
    height: 36,
    borderRadius: 8,
    border: `1px solid ${FB.border}`,
    padding: "0 12px 0 34px",
    fontSize: 13,
    outline: "none",
    color: FB.text,
    backgroundColor: FB.surface,
    boxSizing: "border-box",
  },
  searchIcon: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    color: FB.textMuted,
    fontSize: 18,
  },
  clearFiltersBtn: {
    padding: "6px 12px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    border: `1px solid ${FB.dangerBorder}`,
    backgroundColor: FB.dangerSoft,
    color: FB.danger,
    cursor: "pointer",
  },
  summaryText: { fontSize: 13, color: FB.textMuted, whiteSpace: "nowrap" },
  overlay: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 9999,
  },
};

// Last picked Sınıf, restored when the page is opened again.
const CLASS_STORAGE_KEY = "contentTracking_selectedClassId";

// KPI card tones: "done" (green) | "open" (amber) | "danger" (red) | undefined (neutral).
const KPI_TONES = {
  done: { bg: FB.doneSoft, border: FB.doneBorder, color: FB.done },
  open: { bg: FB.openSoft, border: FB.openBorder, color: FB.open },
  danger: { bg: FB.dangerSoft, border: FB.dangerBorder, color: FB.danger },
  calm: { bg: FB.accentSoft, border: FB.accentBorder, color: FB.accent },
};

// "Waiting" cards always pulse, but the pulse escalates with the count so a
// handful doesn't look like an emergency (animations: contentTracking.css).
// Past 50 it goes full scary-movie. 🎬
const PULSE_TIERS = {
  calm: { tone: "calm", color: FB.accentBorder, caption: "Her şey yolunda 😌" },
  low: { tone: "danger", color: FB.dangerBorder, caption: "Ufak bir kıpırtı var" },
  mid: { tone: "danger", color: FB.dangerBorder, caption: "Birikmeye başladı 👀" },
  high: { tone: "danger", color: FB.danger, caption: "Alarm zilleri çalıyor 🚨" },
  horror: { tone: "danger", color: FB.danger, caption: "Korku filmi modu 👻 arkana bakma" },
};

const pulseTier = (count) => {
  if (count <= 0) return "calm";
  if (count <= 10) return "low";
  if (count <= 25) return "mid";
  if (count <= 50) return "high";
  return "horror";
};

// Envelope check: HTTP 200 → ResponseStatus → ResponseData. DataIsNull = no rows
// for this class (not an error). Returns the rows, or null on failure.
const readRows = (res) => {
  if (!res || res.status !== 200 || !res.data) return null;
  if (res.data.ResponseStatus === ResponseStatus.DataIsNull) return [];
  if (res.data.ResponseStatus !== ResponseStatus.Success) return null;
  return Array.isArray(res.data.ResponseData) ? res.data.ResponseData : null;
};

const ContentTrackingPage = ({ logOutUser }) => {
  const history = useHistory();
  const userRef = useRef(JSON.parse(localStorage.getItem("user")));
  const fetchSeq = useRef(0);

  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState(null);
  const [tabId, setTabId] = useState(TABS[0].id);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);
  const isLoading = rowsLoading || classesLoading;
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ key: null, dir: null });
  const [detailRow, setDetailRow] = useState(null);

  const tab = TABS.find((t) => t.id === tabId) || TABS[0];
  const columns = tab.columns;

  // Both endpoints are needed (Genel Takip combines them), so they load together
  // and the page only shows data when both succeed.
  // Seq guard: a slow response for a previous class must not overwrite a newer one.
  const loadRows = useCallback(async () => {
    const user = userRef.current;
    if (!user || classId == null) return;
    const seq = ++fetchSeq.current;
    setRowsLoading(true);
    try {
      const [questionRes, videoRes] = await Promise.all([
        getQuestionTracking(classId, user.Token),
        getVideoTracking(classId, user.Token),
      ]);
      if (seq !== fetchSeq.current) return;
      const questionRows = readRows(questionRes);
      const videoRows = readRows(videoRes);
      if (questionRows && videoRows) {
        setRows(mergeTrackingRows(questionRows, videoRows));
      } else {
        setRows([]);
        if (!questionRows) {
          toast.error(questionRes?.data?.ResponseMessage || "Soru takip verileri alınamadı.");
        }
        if (!videoRows) {
          toast.error(videoRes?.data?.ResponseMessage || "Ders videosu verileri alınamadı.");
        }
      }
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      setRows([]);
      toast.error("İçerik takip verileri alınamadı.");
    } finally {
      if (seq === fetchSeq.current) setRowsLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (userRef.current === null) {
      localStorage.clear();
      logOutUser();
      history.push("/login");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Class options (CMS/getsegments) → preselect, which then triggers loadRows.
  useEffect(() => {
    const user = userRef.current;
    if (!user) return;
    let cancelled = false;
    const loadClasses = async () => {
      setClassesLoading(true);
      try {
        const res = await GetAllSegments(user.Token);
        if (cancelled) return;
        if (
          res &&
          res.status === 200 &&
          res.data?.ResponseStatus === ResponseStatus.Success &&
          Array.isArray(res.data.ResponseData)
        ) {
          const options = buildClassOptions(res.data.ResponseData);
          setClasses(options);
          // Restore the last picked class if it still exists, else the first one.
          const savedId = localStorage.getItem(CLASS_STORAGE_KEY);
          const saved = options.find((c) => String(c.id) === savedId);
          if (saved) setClassId(saved.id);
          else if (options.length) setClassId(options[0].id);
        } else {
          toast.error(res?.data?.ResponseMessage || "Sınıf listesi alınamadı.");
        }
      } catch (e) {
        if (!cancelled) toast.error("Sınıf listesi alınamadı.");
      } finally {
        if (!cancelled) setClassesLoading(false);
      }
    };
    loadClasses();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const enrichedRows = useMemo(() => {
    const courseLabels = buildCourseLabels(rows);
    return rows.map((row) => ({
      ...row,
      _courseLabel: courseLabels.get(row.CourseId),
      _d: deriveRow(row),
    }));
  }, [rows]);

  // KPI summary follows the Ders filter (only that one — search and the other
  // column filters don't change it).
  const courseFilterIds = filters.CourseId;
  const stats = useMemo(() => {
    const base =
      courseFilterIds == null
        ? enrichedRows
        : enrichedRows.filter((r) => courseFilterIds.includes(r.CourseId));
    const total = base.length;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
    const done = base.filter((r) => r._d.overallDone).length;
    const incomplete = base.filter((r) => r._d.incomplete).length;
    return {
      total,
      done,
      doneRate: pct(done),
      incomplete,
      incompleteRate: pct(incomplete),
      videosAwaitingSystem: base.reduce((n, r) => n + r._d.videosAwaitingSystem, 0),
      awaitingQuestions: base.filter((r) => r._d.awaitingQuestions).length,
    };
  }, [enrichedRows, courseFilterIds]);

  const searchedRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("tr-TR");
    return enrichedRows.filter((row) => matchesSearch(row, term));
  }, [enrichedRows, search]);

  const filteredRows = useMemo(
    () => applyColumnFilters(searchedRows, columns, filters),
    [searchedRows, columns, filters]
  );

  const displayRows = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key);
    if (!col || !sort.dir) return filteredRows;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const va = columnSortValue(col, a);
      const vb = columnSortValue(col, b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * factor;
      return String(va).localeCompare(String(vb), "tr", { numeric: true, sensitivity: "base" }) * factor;
    });
  }, [filteredRows, columns, sort]);

  // Cascading options: each column's choices come from rows filtered by the OTHER
  // columns (+ search), so you can't pick a combination that yields nothing.
  const filterOptions = useMemo(() => {
    const result = {};
    columns.forEach((col) => {
      const base = applyColumnFilters(searchedRows, columns, filters, col.key);
      const byValue = new Map();
      base.forEach((row) => {
        const value = col.value(row);
        if (value != null && value !== "" && !byValue.has(value)) {
          byValue.set(value, columnOptionLabel(col, row));
        }
      });
      result[col.key] = [...byValue.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, "tr", { numeric: true, sensitivity: "base" }));
    });
    return result;
  }, [searchedRows, columns, filters]);

  const courseOptions = useMemo(() => {
    const byId = new Map();
    enrichedRows.forEach((r) => {
      if (!byId.has(r.CourseId)) byId.set(r.CourseId, r._courseLabel);
    });
    return [...byId.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "tr"));
  }, [enrichedRows]);

  const activeFilterCount = columns.filter((c) => filters[c.key] != null).length;

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const handleSort = (key) =>
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });

  const handleClassChange = (e) => {
    const id = Number(e.target.value);
    localStorage.setItem(CLASS_STORAGE_KEY, String(id));
    setClassId(id);
    setFilters({});
    setDetailRow(null);
  };

  // Identity filters (Ders/Konu/Kazanım/Id) carry over; tab-specific ones reset.
  const handleTabChange = (id) => {
    setTabId(id);
    setFilters((prev) =>
      IDENTITY_KEYS.reduce((acc, key) => (prev[key] != null ? { ...acc, [key]: prev[key] } : acc), {})
    );
    setSort((prev) => (IDENTITY_KEYS.includes(prev.key) ? prev : { key: null, dir: null }));
  };

  // Toolbar "Ders" select is a shortcut onto the Ders (CourseId) column filter.
  const courseFilter = filters.CourseId;
  const courseSelectValue =
    courseFilter == null ? "all" : courseFilter.length === 1 ? String(courseFilter[0]) : "multi";

  const handleCourseSelect = (e) => {
    const picked = courseOptions.find((c) => String(c.id) === e.target.value);
    setFilter("CourseId", picked ? [picked.id] : null);
  };

  const className = (classes.find((c) => c.id === classId) || {}).name || "Sınıf";

  const renderKpi = (label, value, hint, tone) => {
    const t = KPI_TONES[tone];
    return (
      <div style={{ ...styles.kpiCard, ...(t ? { backgroundColor: t.bg, borderColor: t.border } : {}) }}>
        <div style={{ ...styles.kpiLabel, ...(t ? { color: t.color } : {}) }}>{label}</div>
        <div style={{ ...styles.kpiValue, ...(t ? { color: t.color } : {}) }}>
          {isLoading ? <CircularProgress size={18} style={{ color: FB.accent }} /> : value}
        </div>
        <div style={styles.kpiHint}>{hint}</div>
      </div>
    );
  };

  // Always-pulsing "waiting" card; intensity, tone and caption follow the count.
  const renderPulseKpi = (label, count, hint) => {
    const tierKey = pulseTier(count);
    const tier = PULSE_TIERS[tierKey];
    const t = KPI_TONES[tier.tone];
    return (
      <div
        className={`ct-kpi-pulse--${tierKey}`}
        title={tier.caption}
        style={{
          ...styles.kpiCard,
          backgroundColor: t.bg,
          borderColor: t.border,
          "--ct-pulse-color": tier.color,
        }}
      >
        <div style={{ ...styles.kpiLabel, color: t.color }}>{label}</div>
        <div
          className={tierKey === "horror" && !isLoading ? "ct-kpi-flicker" : undefined}
          style={{ ...styles.kpiValue, color: t.color }}
        >
          {isLoading ? <CircularProgress size={18} style={{ color: FB.accent }} /> : fmtNum(count)}
        </div>
        <div style={styles.kpiHint}>{hint}</div>
        {!isLoading && <div style={{ ...styles.kpiCaption, color: t.color }}>{tier.caption}</div>}
      </div>
    );
  };

  return (
    <div>
      {isLoading && (
        <div style={styles.overlay}>
          <CircularProgress size={70} style={{ color: FB.accent }} />
        </div>
      )}
      <SideBar />

      <div style={styles.container}>
        <BackButton />
        <div style={styles.titleRow}>
          <div>
            <div style={styles.title}>İçerik Takip</div>
            <div style={styles.subtitle}>
              {className} · Soru ve ders videosu üretim süreçlerini kazanım bazında takip edin.
            </div>
          </div>
          <div style={styles.headerActions}>
            <label style={styles.selectWrap}>
              Sınıf
              <select
                style={styles.select}
                value={classId == null ? "" : classId}
                onChange={handleClassChange}
                disabled={classes.length === 0}
              >
                {classes.length === 0 && <option value="">—</option>}
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <button style={styles.refreshBtn} onClick={loadRows} disabled={classId == null}>
              <RefreshIcon style={{ fontSize: 16 }} /> Yenile
            </button>
          </div>
        </div>

        <div style={styles.kpiRow}>
          {renderKpi(
            "Toplam kazanım",
            fmtNum(stats.total),
            courseFilterIds == null ? "Seçili sınıfta" : "Seçili derslerde"
          )}
          {renderKpi("Genel Done", fmtNum(stats.done), `%${stats.doneRate} tamamlandı`, "done")}
          {renderKpi("Eksik", fmtNum(stats.incomplete), `%${stats.incompleteRate} · Progress %100 değil`, "open")}
          {renderPulseKpi(
            "Sisteme girilmeyi bekleyen video",
            stats.videosAwaitingSystem,
            "Etkileşim onaylı, sisteme hazır değil"
          )}
          {renderPulseKpi("Soru beklenen kazanım", stats.awaitingQuestions, "Video yüklü, 5 soru eksik")}
        </div>

        <div style={styles.tabs} role="tablist" aria-label="Takip görünümü">
          {TABS.map((t) => {
            const Icon = TAB_ICONS[t.id];
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={t.id === tabId}
                style={styles.tab(t.id === tabId)}
                onClick={() => handleTabChange(t.id)}
              >
                <Icon style={{ fontSize: 18 }} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div style={styles.searchRow}>
          <div style={styles.searchLeft}>
            <div style={styles.searchWrap}>
              <SearchIcon style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Kazanım, konu veya AchievementId ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <label style={styles.selectWrap}>
              Ders
              <select
                style={styles.select}
                value={courseSelectValue}
                onChange={handleCourseSelect}
              >
                <option value="all">Tüm Dersler</option>
                {courseSelectValue === "multi" && (
                  <option value="multi" disabled>
                    {courseFilter.length} ders seçili
                  </option>
                )}
                {courseOptions.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            {activeFilterCount > 0 && (
              <button type="button" style={styles.clearFiltersBtn} onClick={() => setFilters({})}>
                ✕ Tüm Filtreleri Temizle ({activeFilterCount})
              </button>
            )}
          </div>
          <span style={styles.summaryText}>
            {isLoading ? "Yükleniyor…" : `${fmtNum(displayRows.length)} / ${fmtNum(enrichedRows.length)} kazanım`}
          </span>
        </div>

        <TrackingTable
          columns={columns}
          rows={displayRows}
          filterOptions={filterOptions}
          filters={filters}
          onFilterChange={setFilter}
          sort={sort}
          onSort={handleSort}
          onOpenDetail={setDetailRow}
          emptyMessage={
            isLoading
              ? "Yükleniyor…"
              : enrichedRows.length === 0
              ? "Bu sınıf için veri bulunamadı."
              : "Arama veya filtrelerle eşleşen kazanım yok."
          }
        />
      </div>

      <DetailDrawer row={detailRow} onClose={() => setDetailRow(null)} />
      <ToastContainer autoClose={5000} />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  logOutUser: () => dispatch(logoutUser()),
});

export default connect(null, mapDispatchToProps)(ContentTrackingPage);
